import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, Zap, Shield, TrendingUp, Activity, Settings } from 'lucide-react';

const AIAgentDashboard = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Real Arbitrum NFT Agents data from live API
  useEffect(() => {
    const fetchAgentsData = async () => {
      try {
        // Try to fetch from live API first
        const response = await fetch('https://arbitrum-nft-agents-production.up.railway.app/api/agents/status');
        if (response.ok) {
          const data = await response.json();
          const agentsData = Object.entries(data.agents).map(([key, agent]) => ({
            id: key,
            name: agent.name || key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
            generation: 'Arbitrum',
            type: key.split('_')[1] || 'base',
            status: agent.status,
            performance: agent.performance,
            tasks: agent.tasks_completed,
            efficiency: agent.efficiency,
            description: getAgentDescription(key)
          }));
          setAgents(agentsData);
        } else {
          throw new Error('API not available');
        }
      } catch (error) {
        console.log('Using fallback data:', error.message);
        // Fallback to static data if API is not available
        const arbitrumNFTAgents = [
          {
            id: 'nft_pricing_agent',
            name: 'NFT Pricing Agent',
            generation: 'Arbitrum',
            type: 'pricing',
            status: 'active',
            performance: 98.5,
            tasks: 1247,
            efficiency: 94.2,
            description: 'Multi-model price prediction and fair value calculation for NFTs'
          },
          {
            id: 'nft_rarity_agent',
            name: 'NFT Rarity Agent',
            generation: 'Arbitrum',
            type: 'rarity',
            status: 'active',
            performance: 96.8,
            tasks: 892,
            efficiency: 91.7,
            description: 'Comprehensive rarity analysis and undervalued NFT detection'
          },
          {
            id: 'nft_trading_agent',
            name: 'NFT Trading Agent',
            generation: 'Arbitrum',
            type: 'trading',
            status: 'active',
            performance: 97.3,
            tasks: 1156,
            efficiency: 93.1,
            description: 'Automated trading, market making, and arbitrage operations'
          },
          {
            id: 'arbitrum_base_agent',
            name: 'Arbitrum Base Agent',
            generation: 'Arbitrum',
            type: 'base',
            status: 'active',
            performance: 99.1,
            tasks: 2156,
            efficiency: 95.8,
            description: 'Foundation agent for all Arbitrum NFT operations and coordination'
          }
        ];
        setAgents(arbitrumNFTAgents);
      }
      setIsLoading(false);
    };

    fetchAgentsData();
  }, []);

  const getAgentDescription = (agentId) => {
    const descriptions = {
      'nft_pricing_agent': 'Multi-model price prediction and fair value calculation for NFTs',
      'nft_rarity_agent': 'Comprehensive rarity analysis and undervalued NFT detection',
      'nft_trading_agent': 'Automated trading, market making, and arbitrage operations',
      'arbitrum_base_agent': 'Foundation agent for all Arbitrum NFT operations and coordination'
    };
    return descriptions[agentId] || 'Specialized AI agent for NFT operations';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'processing': return 'text-yellow-400';
      case 'idle': return 'text-gray-400';
      default: return 'text-red-400';
    }
  };

  const getPerformanceColor = (performance) => {
    if (performance >= 95) return 'text-green-400';
    if (performance >= 90) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="text-center"
      >
        <h2 className="text-3xl font-bold text-white mb-2">Arbitrum NFT Agents</h2>
        <p className="text-gray-400">Monitor and control your specialized NFT AI agents on Arbitrum</p>
      </motion.div>

      {/* Agent Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((agent, index) => (
          <motion.div
            key={agent.id}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-purple-400/50 transition-all duration-300 cursor-pointer"
            onClick={() => setSelectedAgent(agent)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-2 rounded-lg">
                  <Brain className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{agent.name}</h3>
                  <p className="text-sm text-gray-400">{agent.generation}</p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(agent.status)} bg-white/10`}>
                {agent.status}
              </div>
            </div>

            <p className="text-gray-300 text-sm mb-4">{agent.description}</p>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Performance</span>
                <span className={`font-semibold ${getPerformanceColor(agent.performance)}`}>
                  {agent.performance}%
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Tasks Completed</span>
                <span className="text-white font-semibold">{agent.tasks.toLocaleString()}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Efficiency</span>
                <span className="text-white font-semibold">{agent.efficiency}%</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex space-x-2">
                <button className="flex-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-3 py-2 rounded-lg text-sm font-medium transition-colors">
                  <Activity className="h-4 w-4 inline mr-1" />
                  Monitor
                </button>
                <button className="flex-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 px-3 py-2 rounded-lg text-sm font-medium transition-colors">
                  <Settings className="h-4 w-4 inline mr-1" />
                  Configure
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Agent Details Modal */}
      {selectedAgent && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedAgent(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gray-900 rounded-2xl p-6 max-w-2xl w-full border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-lg">
                  <Brain className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">{selectedAgent.name}</h3>
                  <p className="text-gray-400">{selectedAgent.description}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedAgent(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-6">
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                  <span className="text-gray-400">Performance</span>
                </div>
                <div className="text-2xl font-bold text-green-400">{selectedAgent.performance}%</div>
              </div>
              
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Zap className="h-5 w-5 text-yellow-400" />
                  <span className="text-gray-400">Efficiency</span>
                </div>
                <div className="text-2xl font-bold text-yellow-400">{selectedAgent.efficiency}%</div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Recent Activity</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-gray-300">Optimized NFT marketplace gas fees</span>
                  <span className="text-green-400 text-sm">2 min ago</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-gray-300">Analyzed governance proposal</span>
                  <span className="text-blue-400 text-sm">15 min ago</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-gray-300">Updated security parameters</span>
                  <span className="text-purple-400 text-sm">1 hour ago</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default AIAgentDashboard;
